import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import ReactJsonSchemaForm from '@rjsf/core';
import FormBootstrap4 from '@rjsf/bootstrap-4';

/** Cast for React 19 type compatibility with RJSF (expects React 18 ReactNode). */
const Form = FormBootstrap4 as React.ComponentType<any>;
import { customizeValidator } from '@rjsf/validator-ajv8';
import Locales from 'ajv-i18n';
import { withBranding } from '@/hoc/withBranding';
import * as yepcodeTemplates from './templates';
import * as yepcodeWidgets from './widgets';
import * as yepcodeFields from './fields';
import { EmbedFormOptions, GenericObjectType } from '@/types';

export interface YepCodeJsonSchemaFormProps
  extends Omit<React.ComponentProps<typeof ReactJsonSchemaForm>, 'validator'> {
  locale?: string;
  fallbackLocale?: string;
  validator?: React.ComponentProps<typeof ReactJsonSchemaForm>['validator'];
  embedFormOptions?: EmbedFormOptions;
}

const getValidator = (locale?: string, fallbackLocale?: string) => {
  if (locale && locale in Locales) {
    return customizeValidator({}, Locales[locale as keyof typeof Locales]);
  }
  if (fallbackLocale && fallbackLocale in Locales) {
    return customizeValidator(
      {},
      Locales[fallbackLocale as keyof typeof Locales]
    );
  }
  return customizeValidator({}, Locales['en']);
};

const transformErrors = (errors: any[], schema: GenericObjectType) => {
  return errors.map((error) => {
    const property = error.property?.replace(/^\./, '');
    const schemaProperty = schema.properties?.[property];
    if (
      schemaProperty?.errorMessage &&
      schemaProperty.errorMessage[error.name]
    ) {
      error.message = schemaProperty.errorMessage[error.name];
    }
    return error;
  });
};

const YepCodeJsonSchemaForm = ({
  id,
  templates,
  widgets,
  fields,
  locale,
  fallbackLocale,
  validator,
  className,
  ...rest
}: YepCodeJsonSchemaFormProps) => {
  const defaultValidator = useMemo(() => {
    return getValidator(locale, fallbackLocale);
  }, [locale, fallbackLocale]);
  const [formData, setFormData] = useState(rest.formData);
  const onChangeFn = useMemo(() => {
    if (!rest.embedFormOptions?.onChange) {
      return null;
    }
    return new Function(
      'formData',
      'setFormData',
      rest.embedFormOptions?.onChange
    );
  }, [rest.embedFormOptions]);
  const fieldTransformers = useMemo(() => {
    const result: GenericObjectType = {};
    for (const [attribute, options] of Object.entries(
      (rest.embedFormOptions || {}).fields || {}
    )) {
      if (options.transformFn) {
        result[attribute] = new Function('value', options.transformFn);
      }
    }
    if (Object.keys(result).length === 0) {
      return null;
    }
    return result;
  }, [rest.embedFormOptions]);

  return (
    <Form
      id={id}
      idPrefix={`root_${id}`}
      validator={validator || defaultValidator}
      transformErrors={(errors: any[]) => transformErrors(errors, rest.schema)}
      className={classNames([`yepcode-form`, className])}
      fields={{ ...yepcodeFields, ...fields }}
      templates={{ ...yepcodeTemplates, ...templates }}
      widgets={{ ...yepcodeWidgets, ...widgets }}
      {...rest}
      formData={formData}
      onChange={(onChangeEvent: any) => {
        if (rest.onChange) {
          rest.onChange(onChangeEvent);
        }
        if (onChangeFn) {
          onChangeFn(onChangeEvent.formData, setFormData);
        }
        const { schema, formData } = onChangeEvent;
        // Remove required booleans with false value
        if (schema?.required && Array.isArray(schema.required)) {
          for (const requiredAttribute of schema.required) {
            const attributeSchema = (schema.properties || {})[
              requiredAttribute
            ];
            // @ts-ignore
            if (attributeSchema && attributeSchema.type === 'boolean') {
              if (formData[requiredAttribute] === false) {
                delete formData[requiredAttribute];
              }
            }
          }
        }
        if (fieldTransformers) {
          // Process transformers
          for (const attribute in fieldTransformers) {
            const convertFn = fieldTransformers[attribute];
            formData[attribute] = convertFn(formData[attribute]);
          }
        }
        setFormData(formData);
      }}
    />
  );
};

export default withBranding(YepCodeJsonSchemaForm);
